/**
 * CORE-496 — mirror the onboarding tour into the Agapella chat history.
 *
 * The tour is delivered through Agapella's pop-up, so every line she says there
 * must also land in the one shared dialog (spec: «Весь онбординг сохраняется в
 * истории чата Agapella»). Only the server can author a message AS Agapella, so
 * the client posts the text to awesome-chat-api
 * `POST /api/v2/onboarding/tour/log`, which writes it into the 1:1 companion
 * channel with `ai_generated: true` — exactly like her streamed replies.
 *
 * Auth is the same self-service scheme `proactivity.ts` uses: the user's own
 * Bearer JWT, validated server-side via .NET `/api/auth/me`. The `channelKey`
 * comes from `api.ensureAiCompanionChannel()` and is re-checked server-side
 * against the caller, so a client can only write into its own channel.
 *
 * Best-effort: a lost line degrades the transcript, never the tour. Failures
 * are swallowed and never block the UI.
 */

import { CHAT_API_BASE_URL } from '../constants/chat';
import { storage } from '../utils/storage';

/** Which part of the flow produced the line — server-side telemetry only. */
export type TourTranscriptKind = 'step' | 'skip_step' | 'skip_all' | 'close';

export interface TourTranscriptLine {
  userId: string;
  /** `ai-<userId>-<companionId>` from `api.ensureAiCompanionChannel`. */
  channelKey: string;
  text: string;
  kind: TourTranscriptKind;
  /** 1-based step the line belongs to. */
  step?: number;
}

/** Append one Agapella-authored line to the chat history. Returns true on 2xx. */
export async function logTourMessage(line: TourTranscriptLine): Promise<boolean> {
  const { userId, channelKey, text, kind, step } = line;
  if (!userId || !channelKey || !text) return false;

  const token = await storage.getToken();
  if (!token) return false; // self-service auth needs the user's JWT

  const base = CHAT_API_BASE_URL.replace(/\/+$/, '');
  try {
    const res = await fetch(`${base}/api/v2/onboarding/tour/log`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        user_id: userId,
        channel_key: channelKey,
        text,
        kind,
        step: step ?? null,
      }),
    });
    return res.ok;
  } catch {
    return false;
  }
}
